import moment from "moment";
import { useMemo, useState } from "react";
import { Button, Divider, Grid, Popup } from "semantic-ui-react";
import { OpenAPI } from "simplydo/interfaces";
import styled from "styled-components";
import Color from 'color';
import RichText from "components/lib/Editors/RichText";

type CalendarType = OpenAPI.GET<'/challenges/{id}/calendar'>['response']['calendar'];
type Event = CalendarType['events'][0];

type EventWithIndex = Omit<Event, 'from' | 'to'> & ({
  from: Date;
  to?: Date;
  index: number;
});

// MARK: Date helpers
export const datePlusDays = (date, days) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

export const dateStart = (date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

export const dateEnd = (date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
}

// MARK: Cell / Events
type ForegroundStyle = {
  startsToday?: boolean;
  endsToday?: boolean;
  color?: string;
  dummy?: boolean;
  selected?: boolean;
}

const EventStyleForeground = styled.div<ForegroundStyle>`
  ${({ color, selected, dummy, startsToday, endsToday }) => {
    const selectColor = Color(color).darken(0.2).hex();

    return `
      flex: 1;
      cursor: pointer;
      color: #fff;
      font-weight: bold;

      position: relative;
      z-index: 1;

      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      font-size: 12px;

      background-color: ${selected ? selectColor : color || '#2185d0'};
      padding: 0 6px;

      ${dummy ? `
        visibility: hidden;
      ` : ''}

      ${startsToday ? `
        margin-left: 8px;
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
      ` : ''}

      ${endsToday ? `
        margin-right: 8px;
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
      ` : ''}
    `}
  }`;

type EventProps = {
  event: EventWithIndex;
  currDate: Date;
}

type ForegroundEventProps = EventProps & {
  selected: boolean;
  onSelectEvent: (event: EventWithIndex) => void;
  deleteEvent: (eventId: string) => void;
}

const ForegroundEvent = ({ event, currDate, selected, onSelectEvent, deleteEvent }: ForegroundEventProps) => {
  if (event.type === "dummy") {
    return <EventStyleForeground dummy>.</EventStyleForeground>;
  }

  const eventStartsToday = event.from.toDateString() === currDate.toDateString();
  const eventEndsToday = event.to.toDateString() === currDate.toDateString();
  const eventIsMonday = currDate.getDay() === 1;

  return (
    <Popup
      on="click"
      wide="very"
      hideOnScroll={false}
      style={{ zIndex: 200 }}
      onOpen={() => onSelectEvent(event)}
      onClose={() => onSelectEvent(null)}
      trigger={
        <EventStyleForeground
          startsToday={eventStartsToday}
          endsToday={eventEndsToday}
          selected={selected}
          color={event.color}
        >
          {eventStartsToday || eventIsMonday ? `${event.title}` : <span>&nbsp;</span>}
        </EventStyleForeground>
      }
      content={<div>
        <Popup.Header><h3>{event.title}</h3></Popup.Header>
        <Popup.Content>
          {moment(event.from).format("MMM DD, HH:mm")} - {moment(event.to).format("MMM DD, HH:mm")}

          <Divider />

          <RichText
            value={event.description || ''}
            placeholder='No description'
            readOnly
          />

          {event.isGenerated ? null : (
            <div style={{
              marginTop: 16,
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <Button color="red" onClick={() => deleteEvent && event.id && deleteEvent(event.id)}>Delete</Button>
            </div>
          )}
        </Popup.Content>
      </div>}
    />
  );
}

const EventStyleBackground = styled.div<{ event: EventWithIndex }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  color: ${({ color }) => Color(color || '#2185d0').lighten(0.4).hex()};
  padding-left: 4px;
  font-weight: bold;

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ color }) => Color(color || '#2185d0').lighten(0.9).hex()};
`;

const BackgroundEvent = ({ currDate, event }: EventProps) => {
  const eventStartsToday = event.from.toDateString() === currDate.toDateString();
  const eventEndsToday = event.to.toDateString() === currDate.toDateString();

  return (
    <EventStyleBackground event={event}>
      {eventStartsToday || eventEndsToday ? event.title : null}
    </EventStyleBackground>
  )
}

type CellProps = {
  dayDate: Date;
  daysIntoCalendar: number;
  totalDays: number;
  dayNumOfTheWeek: number;
  weekNum: number;
  events: EventWithIndex[];
  onSelectEvent: (event: EventWithIndex) => void;
  selectedEvent: EventWithIndex;
  deleteEvent: (eventId: string) => void;
}

const EventList = styled.div`
  display: flex;
  gap: 4px;
  flex-direction: column;
  padding-bottom: 8px;
`;

const Cell = ({
  dayDate,
  daysIntoCalendar,
  totalDays,
  dayNumOfTheWeek,
  events,
  onSelectEvent,
  selectedEvent,
  deleteEvent
}: CellProps) => {
  const isWeekendDay = dayNumOfTheWeek === 5 || dayNumOfTheWeek === 6;
  const isPaddingBeforeStartDay = daysIntoCalendar < 0;
  const isPaddingAfterEndDay = daysIntoCalendar >= totalDays;

  const isPadding = isPaddingBeforeStartDay || isPaddingAfterEndDay;

  const { foregroundEvents, backgroundEvents } = useMemo<{
    foregroundEvents: EventWithIndex[],
    backgroundEvents: EventWithIndex[]
  }>(() => {
    let foregroundIndex = 0;
    return events.reduce((acc: {
      foregroundEvents: EventWithIndex[],
      backgroundEvents: EventWithIndex[]
    }, event) => {
      if (event.type === "background") {
        acc.backgroundEvents.push(event);
      } else {
        const dummiesToInsert = event.index - foregroundIndex;
        for (let i = 0; i < dummiesToInsert; i++) {
          acc.foregroundEvents.push({ type: "dummy" } as EventWithIndex);
        }
        foregroundIndex = event.index + 1;

        acc.foregroundEvents.push(event);
      }

      return acc;
    }, { foregroundEvents: [], backgroundEvents: [] });
  }, [events]);

  return (
    <Grid.Column
      style={{
        position: 'relative',
        padding: 0,
        background: isPadding ? '#c3c3c3' : isWeekendDay ? '#f1f1f1' : 'white',
      }}
    >
      <div style={{ position: 'relative', zIndex: 1, paddingLeft: 4 }}>
        <span style={{ color: 'grey' }}>
          {moment(dayDate).format("MMM D")} {isPadding || isWeekendDay ? null : <b>Day {daysIntoCalendar + 1}</b>}
        </span>
      </div>
      <EventList>
        {foregroundEvents.map(event => <ForegroundEvent
          key={event.id}
          event={event}
          currDate={dayDate}
          onSelectEvent={onSelectEvent}
          selected={selectedEvent?.id === event.id}
          deleteEvent={deleteEvent}
        />)}
      </EventList>
      {backgroundEvents.map(event => (
        <BackgroundEvent
          key={event.id}
          event={event}
          currDate={dayDate}
        />
      ))}
    </Grid.Column>
  );
}

type CalendarProps = CalendarType & {
  deleteEvent: (eventId: string) => void;
};

// MARK: Calendar
const Calendar = ({ events, start: startAsString, end: endAsString, deleteEvent }: CalendarProps) => {
  const [selectedEvent, setSelectedEvent] = useState(null)

  // Add index and create date objects from range strings
  const [alignedEvents, firstEvent, lastEvent] = useMemo<[EventWithIndex[], EventWithIndex, EventWithIndex]>(() => {
    const withDates = events.map(event => ({
      ...event,
      from: new Date(event.from),
      to: new Date(event.to ?? event.from),
    }));

    withDates.sort((a, b) => a.from.getTime() - b.from.getTime());
    const firstEvent = withDates[0];
    let lastEvent = withDates[0];

    const counts = {};
    const indexedEvents = withDates.flatMap((event) => {
      if (event.to && event.to.getTime() > lastEvent.to.getTime()) {
        lastEvent = event;
      }
      const aligned: EventWithIndex = { index: 0, ...event };

      if (aligned.type === "background") {
        return aligned;
      }
      const eventDays = (aligned.to.getTime() - aligned.from.getTime()) / (1000 * 60 * 60 * 24);

      for (let i = 0; i <= eventDays; i++) {
        const currDate = datePlusDays(aligned.from, i).toDateString();
        counts[currDate] = counts[currDate] || 0;

        if (i === 0) {
          aligned.index = counts[currDate];
        }

        counts[currDate]++;
      }
      return aligned;
    });

    return [indexedEvents, firstEvent as EventWithIndex, lastEvent as EventWithIndex];
  }, [events]);

  const [start, startOffset] = useMemo(() => {
    let asDate = new Date(startAsString);
    if (asDate.getTime() > firstEvent?.from?.getTime()) {
      asDate = firstEvent?.from;
    }

    // pad so that first day is a monday
    const startDay = asDate.getDay();
    if (startDay !== 1) {
      asDate = datePlusDays(asDate, 1 - startDay);
    }
    return [asDate, startDay - 1];
  }, [startAsString, firstEvent]);
  const end = useMemo(() => {
    const asDate = new Date(endAsString);
    if (asDate.getTime() < lastEvent?.to?.getTime()) {
      return lastEvent?.to;
    };
    return asDate;
  }, [endAsString, lastEvent]);
  const daysRange = useMemo(() => 1 + Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)), [start, end]);

  return (
    <Grid celled>
      <Grid.Row columns={6}>
        <Grid.Column><b>Mon</b></Grid.Column>
        <Grid.Column><b>Tue</b></Grid.Column>
        <Grid.Column><b>Wed</b></Grid.Column>
        <Grid.Column><b>Thu</b></Grid.Column>
        <Grid.Column><b>Fri</b></Grid.Column>
        <Grid.Column><b>Sat/Sun</b></Grid.Column>
      </Grid.Row>
      {Array.from({ length: Math.ceil(daysRange / 7) }, (_, weekNum) => {
        return (
          <Grid.Row
            key={`week-${weekNum}`}
            style={{
              minHeight: 100,
            }}
            columns={6}
          >
            {Array.from({ length: 7 }, (_, dayNum) => {
              const dayCounter = weekNum * 7 + dayNum;

              // These props are the same for regular and weekend cells
              const sharedProps = {
                weekNum,
                totalDays: daysRange - startOffset,
                deleteEvent,
                onSelectEvent: setSelectedEvent,
                selectedEvent,
              }

              // Render weekend inside one cell
              if (dayNum === 5) {
                return (
                  <Grid.Column
                    style={{ padding: 0 }}
                    key={`weekend-${weekNum}`}
                  >
                    <Grid celled="internally" style={{ height: '100%' }}>
                      <Grid.Row columns={2}>
                        {Array.from({ length: 2 }, (_, weekendDayNum) => {
                          const weekendDayCounter = dayCounter + weekendDayNum;
                          const weekendDayStart = dateStart(datePlusDays(start, weekendDayCounter));

                          const weekendDayEnd = dateEnd(weekendDayStart);

                          const weekendEvents = alignedEvents.filter(event => event.to >= weekendDayStart && event.from <= weekendDayEnd);

                          return (
                            <Cell
                              key={`weekend-${weekNum}-${weekendDayNum}`}
                              dayDate={weekendDayStart}
                              daysIntoCalendar={weekendDayCounter - startOffset}
                              dayNumOfTheWeek={dayNum + weekendDayNum}
                              events={weekendEvents}
                              {...sharedProps}
                            />
                          );
                        })}
                      </Grid.Row>
                    </Grid>
                  </Grid.Column>
                );
              }
              if (dayNum === 6) {
                return null;
              }

              const dateDayStart = dateStart(datePlusDays(start, dayCounter));
              const dateDayEnd = dateEnd(dateDayStart);

              const events = alignedEvents.filter(event => event.to >= dateDayStart && event.from <= dateDayEnd);

              return (
                <Cell
                  key={`day-${weekNum}-${dayNum}`}
                  dayDate={dateDayStart}
                  daysIntoCalendar={dayCounter - startOffset}
                  dayNumOfTheWeek={dayNum}
                  events={events}
                  {...sharedProps}
                />
              );
            })}
          </Grid.Row>
        );
      })}
    </Grid>
  )
}

export default Calendar;
